import { ProductModelService, Semaphore, System, UserPermissions } from 'ngx-core-services';
import { BehaviorSubject } from 'rxjs';

import {
  SystemPromotionStatusMap,
  PortabilityLoadOptions,
  SystemPromotionStatus,
  Portability,
  PortabilityPaginationOptions,
  PortabilityPaginationState,
} from './base-systems-porting.models';

export class SystemPromotionPaginator {
  private readonly sourceSystems: System[] = [];
  private readonly targetSystems: System[] = [];
  readonly state: PortabilityPaginationState;
  private readonly supportedTypologies = ['inverter', 'enerclic', 'meter', 'connext'];
  private readonly debug = false;

  constructor(private readonly options: PortabilityPaginationOptions) {
    this.state = {
      candidateSystems$: new BehaviorSubject<SystemPromotionStatusMap>({}),
      connextDevices$: new BehaviorSubject<{ [connextId: string]: { devices?: string[]; userLevel?: number } }>({}),
      devices$: new BehaviorSubject<{ [deviceId: string]: { plantId?: string | null } }>({}),
      loading: options.loading$ ? options.loading$ : new BehaviorSubject<boolean>(false),
      defaultPageSize: options.defaultPageSize,
      isUpdated: false,
    };

    this.getConnextDeviceAndUserLevel = this.getConnextDeviceAndUserLevel.bind(this);
    this.setSystemsData = this.setSystemsData.bind(this);

    this.loadPage = (new Semaphore()).wrap(this.loadPage.bind(this));
  }

  setSystemsData(systems: System[]) {
    this.state.loading.next(false);
    this.state.candidateSystems$.next({});
    this.state.connextDevices$.next({});
    this.state.devices$.next({});
    this.state.isUpdated = true;

    this.sourceSystems.length = 0;
    this.sourceSystems.push(...systems);

    this.targetSystems.length = 0;
    this.targetSystems.push(...systems.filter((system) => system.source === this.options.advancedSource));
  }

  async getConnextDeviceAndUserLevel(connextId: string): Promise<{ devices?: string[]; userLevel?: number } | undefined> {
    let connextAttributes =  this.state.connextDevices$.value[connextId];
    const connextDevices = connextAttributes?.devices;
    if (connextDevices) {
      return { devices: connextDevices, userLevel: connextAttributes?.userLevel };
    }

    connextAttributes = await this.options.getConnextDeviceAndUserLevel(connextId).catch((_) => ({} as any));

    const currentConnextDevices = this.state.connextDevices$.value;
    this.state.connextDevices$.next({
      ...currentConnextDevices,
      [connextId]: { ...connextAttributes },
    });
    return connextAttributes;
  }

  private logDebug(message: string, ...args: any[]): void {
    if (this.debug) {
      console.log(message, ...args);
    }
  }

  private getFilteredAndSortedSystems(opts: PortabilityLoadOptions): System[] {
    const filterBaseSystems = opts.filterBaseSystems ?? ((baseSystems: System[]) => baseSystems);
    const filteredSystems = filterBaseSystems(this.sourceSystems);
    this.logDebug('Filtered systems count:', filteredSystems.length);

    const sortFunction = opts.sortFunction ?? ((a: System, b: System) => (a.name || '').localeCompare(b.name || ''));
    const sortedSystems = [...filteredSystems].sort(sortFunction);
    this.logDebug('Filtered systems count after sorting:', sortedSystems.length);

    return sortedSystems;
  }

  private createPromotionStatusFilter(filteredSystems: System[], opts: PortabilityLoadOptions) {
    const defaultCandidateFilter = (candidateSystems: SystemPromotionStatusMap): SystemPromotionStatusMap => {
      const filtered: SystemPromotionStatusMap = {};
      for (const uid in candidateSystems) {
        if (filteredSystems.find(system => system.uid === uid)) {
          filtered[uid] = candidateSystems[uid];
        }
      }
      return filtered;
    };

    return opts.filterCandidateSystems
      ? (candidateSystems: SystemPromotionStatusMap): SystemPromotionStatusMap => {
          return opts.filterCandidateSystems!(defaultCandidateFilter(candidateSystems));
        }
      : defaultCandidateFilter;
  }

  private countPromotableSystems(candidateSystems: SystemPromotionStatusMap, filter: (cs: SystemPromotionStatusMap) => SystemPromotionStatusMap): number {
    return Object.keys(filter(candidateSystems)).length;
  }

  private async processSystem(system: System): Promise<boolean> {
    const systemUid = system.uid;
    if (!systemUid) {
      this.logDebug('Skipped system without uid');
      return false;
    }

    if (this.state.candidateSystems$.value[systemUid]?.isPromotable !== undefined) {
      this.logDebug('Skipping already processed system:', systemUid);
      return false;
    }

    const deviceIds = await this.getSystemDeviceIds(system);
    if (!deviceIds || deviceIds.length === 0) {
      return false;
    }

    await this.fetchDevicePlantIds(deviceIds);
    await this.updateSystemPromotionStatus(system, systemUid, deviceIds);
    return true;
  }

  private async getSystemDeviceIds(system: System): Promise<string[] | undefined> {
    const isConnextSystem = system.configuration.connextId;
    let deviceIds: string[] | undefined = isConnextSystem
      ? (await this.getConnextDeviceAndUserLevel(isConnextSystem))?.devices
      : system.devices.map((device) => device.id.toLocaleLowerCase());

    return deviceIds?.filter((deviceId) => {
      const product = new ProductModelService(deviceId);
      const productType = product.type?.name || 'unknown';
      return this.supportedTypologies.includes(productType);
    });
  }

  private async fetchDevicePlantIds(deviceIds: string[]): Promise<void> {
    const missingDeviceIds = deviceIds.filter(deviceId => {
      const cached = this.state.devices$.value[deviceId];
      return !(cached && cached.plantId !== undefined);
    });
    if (missingDeviceIds.length === 0) return;
    const fetchedPlantIds = await this.options.getDevicesPlantIds(missingDeviceIds).catch(() => ({} as { [deviceId: string]: string | null | undefined }));
    const currentCache = { ...this.state.devices$.value };
    for (const deviceId of missingDeviceIds) {
      const plantId = fetchedPlantIds[deviceId];
      if (plantId !== undefined) {
        currentCache[deviceId] = { plantId };
      }
    }
    this.state.devices$.next(currentCache);
  }

  private async updateSystemPromotionStatus(system: System, systemUid: string, deviceIds: string[]): Promise<void> {
    const isFullyPromoted = deviceIds.every(
      (deviceId) => this.targetSystems.some(
        (_system) => _system.devices.some(
          (device) => device.id.toLocaleLowerCase() === deviceId),
      )
    );

    const isPartiallyPromotedKnown = deviceIds.some(
      (deviceId) => this.targetSystems.some(
        (_system) => _system.devices.some(
          (device) => device.id.toLocaleLowerCase() === deviceId),
      )
    );

    const isPartiallyPromotedUnknown = deviceIds.some(
      (deviceId) => this.targetSystems.some(
        (_system) => _system.uid === this.state.devices$.value[deviceId]?.plantId,
      )
    );

    const isConnext = system.configuration.connextId;
    const isPromotable = deviceIds.every((deviceId) => {
      const userLevel = isConnext ? this.state.connextDevices$.value[isConnext]?.userLevel : UserPermissions.Owner;
      const isDeviceInList = this.state.devices$.value[deviceId];
      return isDeviceInList && isDeviceInList.plantId === null &&
             (userLevel === UserPermissions.Owner || userLevel === UserPermissions.Admin);
    });

    const userLevel = isConnext ? this.state.connextDevices$.value[isConnext]?.userLevel : UserPermissions.Owner;
    const amIAuthorized = isConnext ? (userLevel === UserPermissions.Owner || userLevel === UserPermissions.Admin) : true;
    const portability = this.determinePortability(isPartiallyPromotedUnknown, amIAuthorized, {
      isFullyPromotedOnASystemThatIKnow: isFullyPromoted,
      isPartiallyPromotedOnASystemThatIKnow: isPartiallyPromotedKnown,
      isPromotable: isPromotable,
      isPartiallyPromotedOnASystemThatIDontKnow: isPartiallyPromotedUnknown,
    });

    this.state.candidateSystems$.next({
      ...this.state.candidateSystems$.value,
      [systemUid]: {
        deviceIds,
        isPromotable: portability === Portability.PORTABLE,
        isFullyPromotedOnASystemThatIKnow: isFullyPromoted,
        isPartiallyPromotedOnASystemThatIKnow: isPartiallyPromotedKnown,
        isPartiallyPromotedOnASystemThatIDontKnow: isPartiallyPromotedUnknown,
        portability,
      },
    });
  }

  private createPromotionResultFromSystem(system: System, promotionStatus: SystemPromotionStatus) {
    return {
      system: system,
      selected$: new BehaviorSubject<boolean>(false),
      portability: promotionStatus.portability,
      toggleSelection() {
        if (this.portability === Portability.PORTABLE) {
          const currentState = this.selected$.value;
          this.selected$.next(!currentState);
        }
      },
    };
  }

  private determinePortability(
    isPartiallyPromotedOnASystemThatIDontKnow: boolean,
    amIAuthorized: boolean,
    promotionStatus: SystemPromotionStatus
  ): Portability {
    if (isPartiallyPromotedOnASystemThatIDontKnow || !amIAuthorized) {
      return Portability.NOT_PORTABLE;
    }

    if (promotionStatus.isFullyPromotedOnASystemThatIKnow) {
      return Portability.FULLY_PORTED;
    }

    if (!promotionStatus.isPromotable) {
      return Portability.ALREADY_PORTED_WITH_ALL_DEVICES;
    }

    if (promotionStatus.isPartiallyPromotedOnASystemThatIKnow) {
      return Portability.ALREADY_PORTED_WITH_SOME_DEVICES;
    }

    return Portability.PORTABLE;
  }

  async loadPage(opts: PortabilityLoadOptions = {}) {
    this.logDebug('Starting load with opts:', opts);
    const pageSize = opts.pageSize ?? this.state.defaultPageSize;
    const currentPage = opts.currentPage ?? 1;
    this.logDebug('Initial pageSize:', pageSize, 'currentPage:', currentPage, 'Total sourceSystems:', this.sourceSystems.length);

    if (this.state.loading.value) {
      throw new Error('Already loading');
    }
    this.state.loading.next(true);

    try {
      const filteredSystems = this.getFilteredAndSortedSystems(opts);
      const combinedCandidateFilter = this.createPromotionStatusFilter(filteredSystems, opts);
      const candidateCount = (candidateSystems: SystemPromotionStatusMap): number =>
        this.countPromotableSystems(candidateSystems, combinedCandidateFilter);
      this.logDebug('Initial candidate count:', candidateCount(this.state.candidateSystems$.value));

      let systemIndex = 0;
      while (
        candidateCount(this.state.candidateSystems$.value) < (pageSize * currentPage) &&
        systemIndex < filteredSystems.length
      ) {
        this.logDebug('While loop iteration. systemIndex:', systemIndex, 'Candidate count:',
          candidateCount(this.state.candidateSystems$.value));

        await this.processSystem(filteredSystems[systemIndex]);
        systemIndex++;
      }

      const candidateSystems = combinedCandidateFilter(this.state.candidateSystems$.value);
      this.logDebug('Candidate systems after filtering:', Object.keys(candidateSystems));

      const fullResult = filteredSystems
        .filter(system => candidateSystems.hasOwnProperty(system.uid!))
        .map(system => this.createPromotionResultFromSystem(system, candidateSystems[system.uid!]));
      this.logDebug('Full result count:', fullResult.length);

      const hasMore = (systemIndex < filteredSystems.length) || (fullResult.length > currentPage * pageSize);
      if (opts.hasMore) {
        opts.hasMore(hasMore);
      }

      const startIndex = (currentPage - 1) * pageSize;
      const pagedResult = fullResult.slice(startIndex, startIndex + pageSize);
      this.logDebug('Returning paged result count:', pagedResult.length);
      return pagedResult;

    } finally {
      this.state.loading.next(false);
    }
  }
}
